import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Divider, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography, 
  useMediaQuery, 
  useTheme 
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as InvoiceIcon,
  Gavel as BidIcon,
  Payment as TransactionIcon,
  Business as CompanyIcon,
  People as UserIcon,
  ExitToApp as LogoutIcon,
  Person as ProfileIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import EmailVerificationBanner from '../EmailVerificationBanner';

const drawerWidth = 240;

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAdmin, isBuyer, isSeller, logout } = useAuth();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define menu items based on user role
  const menuItems = [
    { text: 'Panel de Control', icon: <DashboardIcon />, path: '/dashboard', visible: true },
    { text: 'Mi Perfil', icon: <ProfileIcon />, path: '/profile', visible: true },
    { text: 'Mis Facturas', icon: <InvoiceIcon />, path: '/invoices', visible: isSeller },
    { text: 'Subir Factura', icon: <InvoiceIcon />, path: '/invoices/upload', visible: isSeller },
    { text: 'Mercado', icon: <BidIcon />, path: '/marketplace', visible: isBuyer },
    { text: 'Mis Ofertas', icon: <BidIcon />, path: '/bids', visible: isBuyer },
    { text: 'Transacciones', icon: <TransactionIcon />, path: '/transactions', visible: isBuyer || isSeller || isAdmin },
    // Admin menu items
    { text: 'Panel de Administrador', icon: <DashboardIcon />, path: '/dashboard', visible: isAdmin },
    { text: 'Empresas', icon: <CompanyIcon />, path: '/companies', visible: isAdmin },
    { text: 'Usuarios', icon: <UserIcon />, path: '/users', visible: isAdmin },
    { text: 'Verificar Facturas', icon: <InvoiceIcon />, path: '/admin/invoices', visible: isAdmin },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Facto
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter(item => item.visible)
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton onClick={() => handleNavigation(item.path)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Cerrar Sesión" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {user?.full_name || 'Usuario'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // AppBar height
        }}
      >
        {/* Email verification banner for unverified users */}
        <EmailVerificationBanner />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
